import { lazy, Suspense, useState, useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

import ErrorBoundary from './components/ErrorBoundary';
import Layout           from './components/Layout';
import EventLayout      from './components/EventLayout';

// Eagerly load tiny/frequently-used components (not pages)
import LoginForm        from './components/LoginForm';
import AdminDashboard   from './components/AdminDashboard';
import VolunteerScanner from './components/VolunteerScanner';
import SponsorScanner   from './components/SponsorScanner';
import TokenWallet      from './components/TokenWallet';

// Lazy page imports
const EventsListPage     = lazy(() => import('./pages/EventsListPage'));
const EventWizardPage    = lazy(() => import('./pages/EventWizardPage'));
const ZonesConfigPage    = lazy(() => import('./pages/ZonesConfigPage'));
const RegisterPage       = lazy(() => import('./pages/RegisterPage'));
const AttendeeListPage   = lazy(() => import('./pages/AttendeeListPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const LeadDashboard      = lazy(() => import('./pages/LeadDashboard'));
const ProfilePage        = lazy(() => import('./pages/ProfilePage'));
const LandingPage        = lazy(() => import('./pages/LandingPage'));
const PlatformDashboard  = lazy(() => import('./pages/PlatformDashboard'));
const TokenPricingPage   = lazy(() => import('./pages/TokenPricingPage'));
const PublicEventPage    = lazy(() => import('./pages/PublicEventPage'));
const TicketPage         = lazy(() => import('./pages/TicketPage'));
const OrgSettingsPage    = lazy(() => import('./pages/OrgSettingsPage'));
const AnalyticsPage        = lazy(() => import('./pages/AnalyticsPage'));
const LiveDashboardPage    = lazy(() => import('./pages/LiveDashboardPage'));
const DiscoverPage         = lazy(() => import('./pages/DiscoverPage'));
const PlatformRevenuePage  = lazy(() => import('./pages/PlatformRevenuePage'));
const PlatformSettingsPage = lazy(() => import('./pages/PlatformSettingsPage'));
const NotFoundPage         = lazy(() => import('./pages/NotFoundPage'));

import './styles/theme.css';
import './styles/animations.css';

// Page-level loading fallback
function PageSpinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
      <div style={{
        width: 36, height: 36,
        border: '3px solid var(--line,#ECE6F2)',
        borderTopColor: 'var(--primary,#C81E78)',
        borderRadius: '50%', animation: 'spin 0.8s linear infinite',
      }} />
    </div>
  );
}

function Spinner() {
  return (
    <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
      <div style={{
        width: 40, height: 40,
        border: '4px solid #E2E4ED', borderTopColor: '#6C63FF',
        borderRadius: '50%', animation: 'spin 1s linear infinite',
      }} />
    </div>
  );
}

// Wrapper helpers
function DashboardWrapper()  { const { id } = useParams();     return <AdminDashboard     eventId={Number(id) || 1} />; }
function AttendeeWrapper()   { const { id } = useParams();     return <AttendeeListPage   eventId={Number(id)}      />; }
function AnalyticsWrapper()  { const { id } = useParams();     return <AnalyticsPage      eventId={Number(id)}      />; }
function LiveDashWrapper()   { const { id } = useParams();     return <LiveDashboardPage  eventId={Number(id)}      />; }
function ScannerWrapper()    { return <VolunteerScanner />; }
function SponsorWrapper()    { const { zoneId } = useParams(); return <SponsorScanner   zoneId={Number(zoneId) || 1} />; }

function EventIdRouter() {
  const { id } = useParams();
  const [dest, setDest] = useState(null);
  useEffect(() => {
    fetch('/api/events/' + id, { credentials: 'include' })
      .then(r => r.ok ? r.json() : null)
      .then(data => {
        const status = data?.computed_status ?? data?.status ?? 'draft';
        setDest(['draft'].includes(status) ? 'edit' : 'dashboard');
      })
      .catch(() => setDest('dashboard'));
  }, [id]);
  if (!dest) return null;
  return <Navigate to={'/events/' + id + '/' + dest} replace />;
}

// Auth guards
function PrivateRoute({ children, allowedRoles }) {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user)   return <Navigate to="/login" replace />;
  if (allowedRoles && !allowedRoles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function LayoutRoute({ children, allowedRoles }) {
  return (
    <PrivateRoute allowedRoles={allowedRoles}>
      <Layout>{children}</Layout>
    </PrivateRoute>
  );
}

function EventLayoutRoute({ children, allowedRoles }) {
  return (
    <PrivateRoute allowedRoles={allowedRoles}>
      <Layout>
        <EventLayout>{children}</EventLayout>
      </Layout>
    </PrivateRoute>
  );
}

function PublicOrRedirect() {
  const { user, loading } = useAuth();
  if (loading) return <Spinner />;
  if (!user)   return <LandingPage />;
  if (user.role === 'platform_admin') return <Navigate to="/dashboard" replace />;
  if (['org_admin', 'event_admin'].includes(user.role)) return <Navigate to="/events" replace />;
  if (user.role === 'volunteer') return <Navigate to="/scan" replace />;
  if (user.role === 'sponsor')   return <Navigate to="/sponsor" replace />;
  return <LandingPage />;
}

// Routes
function AppRoutes() {
  const ADMIN = ['org_admin','event_admin','platform_admin'];

  return (
    <Suspense fallback={<PageSpinner />}>
      <Routes>
        {/* Public */}
        <Route path="/"                  element={<PublicOrRedirect />} />
        <Route path="/login"             element={<LoginForm />} />
        <Route path="/register/:eventId" element={<RegisterPage />} />
        <Route path="/e/:slug"           element={<PublicEventPage />} />
        <Route path="/ticket/:token"     element={<TicketPage />} />
        <Route path="/discover"          element={<DiscoverPage />} />

        {/* Events (org admin) */}
        <Route path="/events" element={<LayoutRoute allowedRoles={ADMIN}><EventsListPage /></LayoutRoute>} />
        <Route path="/events/new" element={<LayoutRoute allowedRoles={ADMIN}><EventWizardPage /></LayoutRoute>} />
        <Route path="/events/:id/edit" element={<EventLayoutRoute allowedRoles={ADMIN}><EventWizardPage /></EventLayoutRoute>} />
        <Route path="/events/:id" element={<EventLayoutRoute allowedRoles={ADMIN}><EventIdRouter /></EventLayoutRoute>} />
        <Route path="/events/:id/team" element={<EventLayoutRoute allowedRoles={ADMIN}><UserManagementPage /></EventLayoutRoute>} />
        <Route path="/events/:id/leads" element={<EventLayoutRoute allowedRoles={[...ADMIN,'sponsor']}><LeadDashboard /></EventLayoutRoute>} />
        <Route path="/events/:id/configure" element={<EventLayoutRoute allowedRoles={ADMIN}><ZonesConfigPage /></EventLayoutRoute>} />
        <Route path="/events/:id/dashboard" element={<EventLayoutRoute allowedRoles={ADMIN}><DashboardWrapper /></EventLayoutRoute>} />
        <Route path="/events/:id/attendees" element={<EventLayoutRoute allowedRoles={ADMIN}><AttendeeWrapper /></EventLayoutRoute>} />
        <Route path="/events/:id/analytics" element={<EventLayoutRoute allowedRoles={ADMIN}><AnalyticsWrapper /></EventLayoutRoute>} />
        <Route path="/events/:id/live"      element={<EventLayoutRoute allowedRoles={ADMIN}><LiveDashWrapper /></EventLayoutRoute>} />

        {/* Team (org-level) */}
        <Route path="/team" element={<LayoutRoute allowedRoles={['org_admin','platform_admin']}><UserManagementPage /></LayoutRoute>} />

        {/* Platform admin */}
        <Route path="/dashboard"     element={<LayoutRoute allowedRoles={['platform_admin']}><PlatformDashboard /></LayoutRoute>} />
        <Route path="/admin/pricing" element={<LayoutRoute allowedRoles={['platform_admin']}><TokenPricingPage /></LayoutRoute>} />
        <Route path="/admin/revenue"   element={<LayoutRoute allowedRoles={['platform_admin']}><PlatformRevenuePage /></LayoutRoute>} />
        <Route path="/platform/revenue"  element={<LayoutRoute allowedRoles={['platform_admin']}><PlatformRevenuePage /></LayoutRoute>} />
        <Route path="/platform/settings" element={<LayoutRoute allowedRoles={['platform_admin']}><PlatformSettingsPage /></LayoutRoute>} />
        <Route path="/admin/platform-settings" element={<LayoutRoute allowedRoles={['platform_admin']}><PlatformSettingsPage /></LayoutRoute>} />

        {/* Org */}
        <Route path="/wallet"      element={<LayoutRoute allowedRoles={['org_admin','platform_admin']}><TokenWallet /></LayoutRoute>} />
        <Route path="/org/settings" element={<LayoutRoute allowedRoles={['org_admin']}><OrgSettingsPage /></LayoutRoute>} />

        {/* Volunteer / sponsor */}
        <Route path="/scan/event/:eventId" element={<PrivateRoute allowedRoles={['volunteer','event_admin','org_admin']}><ScannerWrapper /></PrivateRoute>} />
        <Route path="/scan"                element={<PrivateRoute allowedRoles={['volunteer','event_admin','org_admin']}><ScannerWrapper /></PrivateRoute>} />
        <Route path="/sponsor/:zoneId"     element={<PrivateRoute allowedRoles={['sponsor','event_admin','org_admin']}><SponsorWrapper /></PrivateRoute>} />
        <Route path="/sponsor"             element={<PrivateRoute allowedRoles={['sponsor','event_admin','org_admin']}><SponsorWrapper /></PrivateRoute>} />

        {/* Profile */}
        <Route path="/profile" element={<LayoutRoute><ProfilePage /></LayoutRoute>} />

        {/* 404 catch-all */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <ThemeProvider>
        <AuthProvider>
          <ErrorBoundary>
            <AppRoutes />
          </ErrorBoundary>
        </AuthProvider>
      </ThemeProvider>
    </BrowserRouter>
  );
}
